/**
 * Shared event details summary and related tabs (participants, volunteer opportunities).
 * Used by EventDetailModal from Events, Event Participants, and Close Event Record.
 */
import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { getRepeatLabel } from '../shared/utils/repeatOptions';
import { getRegistrationStatusKey, REGISTRATION_STATUS_CONFIG } from '../shared/utils/eventRegistrationMetrics';
import { IconDelete, iconBtnStyle } from '../shared/icons/IconLib';
import { TableActionColumnHeader } from './TableShell';
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { formatTimestampForTable } from '../shared/utils/dateDisplay';
import { displayEmailOrDash } from '../shared/utils/displayEmail';
import { filterVolunteerOpportunitiesByEventId } from '../shared/utils/volunteerOpportunityFromEvent';
import {
  getOpportunitySignupCount,
  getOpportunityVolunteersNeeded,
} from '../shared/utils/volunteerOpportunityDisplay';
import { EVENT_PLACEHOLDER_TBA, isPlaceholderEvent } from '../shared/utils/eventPlaceholder';
import EventPriceBadge from './EventPriceBadge';
import KidHoursTrackingStatus from './KidHoursTrackingStatus';
import { createEventRegistrationDefaultSort } from '../shared/utils/eventParticipantSort';
import LinkifiedText from './LinkifiedText';

const SORTABLE_KEYS = ['participant_name', 'registered_at', 'reg_type', 'attendance', 'email', 'phone'];

/** Same day-boundary rule as Event QR Check-in / Event Participants (Registered vs Walk-In). */
function getParticipantRegTypeCell(participant, event) {
  const existing = (participant?.registration_type != null ? String(participant.registration_type) : '').trim();
  if (existing) return existing;
  const reg = participant?.registration_date;
  const start = event?.start_datetime;
  if (!reg || !start) return '';
  const regD = dayjs(reg);
  const startD = dayjs(start);
  if (!regD.isValid() || !startD.isValid()) return '';
  const dayDiff = startD.startOf('day').diff(regD.startOf('day'), 'day');
  if (dayDiff >= 1) return 'Registered';
  if (dayDiff === 0) return 'Walk-In';
  return '';
}

function getParticipantDisplayName(p) {
  const name = [p?.first_name, p?.last_name].filter(Boolean).map((s) => String(s).trim()).join(' ').trim();
  return name || '—';
}

/** Sort key: last name, first name (stable, matches other event tables). */
function getParticipantNameForSort(p) {
  return [p?.last_name, p?.first_name].filter(Boolean).join(', ').trim().toLowerCase();
}

/** Registration time for display/sort. */
function getParticipantRegisteredAtRaw(p) {
  return p?.registration_date ?? null;
}

function formatParticipantRegisteredAt(p) {
  return formatTimestampForTable(getParticipantRegisteredAtRaw(p));
}

function sortParticipants(list, key, direction, event) {
  if (!key || !list.length) return list;
  const dir = direction === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    if (key === 'registered_at') {
      const t = (p) => {
        const raw = getParticipantRegisteredAtRaw(p);
        if (raw == null || raw === '') return 0;
        const ms = new Date(raw).getTime();
        return Number.isNaN(ms) ? 0 : ms;
      };
      return (t(a) - t(b)) * dir;
    }
    if (key === 'participant_name') {
      const av = getParticipantNameForSort(a);
      const bv = getParticipantNameForSort(b);
      const cmp = av.localeCompare(bv, undefined, { numeric: true });
      return cmp * dir;
    }
    if (key === 'reg_type') {
      const av = (getParticipantRegTypeCell(a, event) || '').toLowerCase();
      const bv = (getParticipantRegTypeCell(b, event) || '').toLowerCase();
      return av.localeCompare(bv, undefined, { numeric: true }) * dir;
    }
    const av = (a[key] ?? '').toString().toLowerCase();
    const bv = (b[key] ?? '').toString().toLowerCase();
    const cmp = av.localeCompare(bv, undefined, { numeric: true });
    return cmp * dir;
  });
}

const labelStyle = { fontWeight: 600, color: '#495057', marginRight: '6px' };
const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 16px', marginBottom: '10px' };
const cellStyle = { marginBottom: '6px' };
const tableWrapStyle = { overflowX: 'auto', maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '12px' };
const thStyle = { padding: '4px 6px', textAlign: 'left', fontWeight: 600 };
const tdStyle = { padding: '4px 6px' };
const tdNameStyle = { ...tdStyle, whiteSpace: 'nowrap' };
const tdPhoneStyle = { ...tdStyle, whiteSpace: 'nowrap' };
const theadTrStyle = { borderBottom: '1px solid #dee2e6', background: '#f8f9fa' };
const tbodyTrStyle = { borderBottom: '1px solid #eee' };
const tabBtnStyle = (active) => ({
  padding: '4px 10px', fontSize: '12px', fontWeight: 600, border: '1px solid #dee2e6', borderRadius: '4px',
  background: active ? '#0d6efd' : '#fff', color: active ? '#fff' : '#495057', cursor: 'pointer',
});

function EventDetailsSummary({ event, programName, organizerName, kidHoursTracked = null }) {
  if (!event) return null;
  const programDisplay = (programName != null && programName !== '') ? programName : (event.program_name || '—');
  const organizerDisplay = (organizerName != null && organizerName !== '') ? organizerName : (event.organizer_name || '—');
  const capacityStatusKey = getRegistrationStatusKey(event);
  const capacityCfg = capacityStatusKey ? (REGISTRATION_STATUS_CONFIG[capacityStatusKey] || REGISTRATION_STATUS_CONFIG.no_cap) : null;
  const placeholder = isPlaceholderEvent(event);
  const startDisplay = placeholder && !event.start_datetime
    ? EVENT_PLACEHOLDER_TBA
    : (event.start_datetime ? dayjs(event.start_datetime).format('MM/DD/YYYY h:mm A') : '—');
  const endDisplay = placeholder && !event.end_datetime
    ? EVENT_PLACEHOLDER_TBA
    : (event.end_datetime ? dayjs(event.end_datetime).format('MM/DD/YYYY h:mm A') : '—');
  const locationDisplay = (event.location || '').trim() || (placeholder ? EVENT_PLACEHOLDER_TBA : '—');

  return (
    <>
      <div style={gridStyle}>
        <div style={cellStyle}><span style={labelStyle}>Name:</span><span>{event.name || '—'}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Location:</span><span style={{ wordBreak: 'break-word' }}>{locationDisplay}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Program:</span><span>{programDisplay}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Start:</span><span>{startDisplay}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Hosted By:</span><span>{organizerDisplay}</span></div>
        <div style={cellStyle}>
          <span style={labelStyle}>Status:</span>
          <span>{event.status || 'Open'}</span>
        </div>
        <div style={cellStyle}><span style={labelStyle}>End:</span><span>{endDisplay}</span></div>
        <div style={cellStyle}>
          <span style={labelStyle}>Capacity:</span>
          <span>
            {event.max_capacity != null ? event.max_capacity : '—'}
            {capacityCfg && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1px 8px',
                  borderRadius: '9999px',
                  backgroundColor: capacityCfg.color,
                  fontSize: '11px',
                  color: '#0f172a',
                  marginLeft: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                {capacityCfg.label}
              </span>
            )}
          </span>
        </div>
        <div style={cellStyle}><span style={labelStyle}>Visibility:</span><span>{event.visibility === 'private' ? 'Private' : 'Public'}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Repeat:</span><span>{getRepeatLabel(event.repeat_rule)}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Audience Type:</span><span>{['Youth Only', 'Adult Only', 'All Ages'].includes(event.audience_type) ? event.audience_type : 'Youth Only'}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Perfect For:</span><span>{(event.perfect_for != null ? String(event.perfect_for) : '').trim() || '—'}</span></div>
        <div style={cellStyle}><span style={labelStyle}>Price ($):</span><EventPriceBadge event={event} /></div>
        {kidHoursTracked != null ? (
          <div style={cellStyle}>
            <KidHoursTrackingStatus tracked={kidHoursTracked} />
          </div>
        ) : null}
      </div>
      <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '8px', marginBottom: '10px' }}>
        <div style={{ fontWeight: 600, color: '#495057', marginBottom: '4px', fontSize: '12px' }}>Description</div>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {event.description ? <LinkifiedText text={event.description} /> : '—'}
        </div>
      </div>
    </>
  );
}

function formatVolunteerOpportunityDatetime(value) {
  if (value == null || value === '') return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MM/DD/YYYY h:mm A') : '—';
}

function EventDetailsParticipantTabs({
  participants = [],
  event: eventForRegType = null,
  loading,
  tab,
  onTabChange,
  readOnly = true,
  selectedParticipantIds,
  onSelectAll,
  onSelectOne,
  onRemoveSelected,
  onRemoveOne,
  removing = false,
  volunteerOpportunities = [],
  volunteerLoading = false,
  showVolunteerTab = false,
  onOpenVolunteerOpportunity,
}) {
  const participantsList = (participants || []).filter((p) => {
    const att = (p.attendance || '').trim();
    return att !== 'Waitlisted' && att !== 'Cancelled';
  });
  const waitlistedList = (participants || []).filter((p) => (p.attendance || '').trim() === 'Waitlisted');
  const cancelledList = (participants || []).filter((p) => (p.attendance || '').trim() === 'Cancelled');
  const selectedIds = selectedParticipantIds || new Set();

  const [sortConfig, setSortConfig] = useState(() => createEventRegistrationDefaultSort('registered_at'));
  const handleSort = (key) => {
    if (!SORTABLE_KEYS.includes(key)) return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  const sortIndicator = (key) =>
    sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : '';

  const sortedParticipantsList = useMemo(
    () => sortParticipants(participantsList, sortConfig.key, sortConfig.direction, eventForRegType),
    [participantsList, sortConfig.key, sortConfig.direction, eventForRegType]
  );
  const sortedWaitlistedList = useMemo(
    () => sortParticipants(waitlistedList, sortConfig.key, sortConfig.direction, eventForRegType),
    [waitlistedList, sortConfig.key, sortConfig.direction, eventForRegType]
  );
  const sortedCancelledList = useMemo(
    () => sortParticipants(cancelledList, sortConfig.key, sortConfig.direction, eventForRegType),
    [cancelledList, sortConfig.key, sortConfig.direction, eventForRegType]
  );

  const setTab = (t) => onTabChange && onTabChange(t);

  const sortableThStyle = (key) =>
    SORTABLE_KEYS.includes(key)
      ? { ...thStyle, cursor: 'pointer', userSelect: 'none' }
      : thStyle;

  const volunteerList = useMemo(
    () => filterVolunteerOpportunitiesByEventId(volunteerOpportunities, eventForRegType?._id),
    [volunteerOpportunities, eventForRegType?._id]
  );
  const isVolunteerTab = tab === 'volunteering';

  if (loading && !isVolunteerTab) return <p style={{ color: '#6c757d', margin: 0 }}>Loading…</p>;

  return (
    <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '8px', marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setTab('participants')} style={tabBtnStyle(tab === 'participants')}>Registered ({participantsList.length})</button>
        <button type="button" onClick={() => setTab('waitlisted')} style={tabBtnStyle(tab === 'waitlisted')}>Waitlisted ({waitlistedList.length})</button>
        <button type="button" onClick={() => setTab('cancelled')} style={tabBtnStyle(tab === 'cancelled')}>Cancelled ({cancelledList.length})</button>
        {showVolunteerTab ? (
          <button type="button" onClick={() => setTab('volunteering')} style={tabBtnStyle(tab === 'volunteering')}>
            Volunteering ({volunteerList.length})
          </button>
        ) : null}
      </div>
      {isVolunteerTab && (
        volunteerLoading ? (
          <p style={{ color: '#6c757d', margin: 0 }}>Loading…</p>
        ) : volunteerList.length === 0 ? (
          <p style={{ color: '#6c757d', margin: 0 }}>No volunteer opportunities linked to this event.</p>
        ) : (
          <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr style={theadTrStyle}>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Volunteers needed</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Signed up</th>
                    <th style={thStyle}>Start</th>
                    <th style={thStyle}>End</th>
                  </tr>
                </thead>
                <tbody>
                  {volunteerList.map((opp) => (
                    <tr key={opp._id} style={tbodyTrStyle}>
                      <td style={tdNameStyle}>
                        {onOpenVolunteerOpportunity ? (
                          <button
                            type="button"
                            onClick={() => onOpenVolunteerOpportunity(opp)}
                            className="btn-link"
                            style={{ padding: 0, border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                          >
                            {opp.name || '—'}
                          </button>
                        ) : (
                          opp.name || '—'
                        )}
                      </td>
                      <td style={tdStyle}>{opp.category || '—'}</td>
                      <td style={tdStyle}>{opp.status || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{getOpportunityVolunteersNeeded(opp)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{getOpportunitySignupCount(opp)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatVolunteerOpportunityDatetime(opp.start_datetime)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatVolunteerOpportunityDatetime(opp.end_datetime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )
      )}
      {tab === 'participants' && (
        participantsList.length === 0 ? (
          <p style={{ color: '#6c757d', margin: 0 }}>No registered participants with a spot (registered, attended, walk-in, or no-show).</p>
        ) : (
          <>
            {!readOnly && selectedIds.size > 0 && onRemoveSelected && (
              <div style={{ marginBottom: '8px' }}>
                <button type="button" disabled={removing} onClick={onRemoveSelected} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #dc3545', borderRadius: '4px', background: '#dc3545', color: '#fff', cursor: removing ? 'not-allowed' : 'pointer' }}>
                  {removing ? 'Removing…' : `Remove selected (${selectedIds.size})`}
                </button>
              </div>
            )}
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr style={theadTrStyle}>
                    {!readOnly && (
                      <th style={{ ...thStyle, width: '36px' }}>
                        <input type="checkbox" checked={participantsList.length > 0 && participantsList.every((p) => selectedIds.has(p._id))} onChange={(e) => onSelectAll && onSelectAll(participantsList, e.target.checked)} aria-label="Select all" />
                      </th>
                    )}
                    <th style={sortableThStyle('participant_name')} onClick={() => handleSort('participant_name')}>Participant{sortIndicator('participant_name')}</th>
                    <th style={sortableThStyle('registered_at')} onClick={() => handleSort('registered_at')}>Registered{sortIndicator('registered_at')}</th>
                    <th style={sortableThStyle('reg_type')} onClick={() => handleSort('reg_type')}>Reg Type{sortIndicator('reg_type')}</th>
                    <th style={sortableThStyle('attendance')} onClick={() => handleSort('attendance')}>Attendance{sortIndicator('attendance')}</th>
                    <th style={sortableThStyle('email')} onClick={() => handleSort('email')}>Email{sortIndicator('email')}</th>
                    <th style={sortableThStyle('phone')} onClick={() => handleSort('phone')}>Phone{sortIndicator('phone')}</th>
                    {!readOnly && onRemoveOne && (
                      <TableActionColumnHeader align="left" style={{ ...thStyle, width: '70px' }} />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedParticipantsList.map((g) => (
                    <tr key={g._id} style={tbodyTrStyle}>
                      {!readOnly && (
                        <td style={tdStyle}>
                          <input type="checkbox" checked={selectedIds.has(g._id)} onChange={(e) => onSelectOne && onSelectOne(g._id, e.target.checked)} aria-label={`Select ${getParticipantDisplayName(g)}`} />
                        </td>
                      )}
                      <td style={tdNameStyle}>{getParticipantDisplayName(g)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatParticipantRegisteredAt(g)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{(getParticipantRegTypeCell(g, eventForRegType) || '').trim() || '—'}</td>
                      <td style={tdStyle}>{g.attendance || 'Registered'}</td>
                      <td style={tdStyle}>{displayEmailOrDash(g.email)}</td>
                      <td style={tdPhoneStyle}>{formatPhoneDisplay(g.phone)}</td>
                      {!readOnly && onRemoveOne && (
                        <td style={tdStyle}>
                          <button type="button" onClick={() => onRemoveOne(g._id)} style={{ ...iconBtnStyle, padding: '2px 6px', border: '1px solid #dc3545', borderRadius: '4px', background: '#fff', color: '#dc3545', cursor: 'pointer' }} title="Remove from event"><IconDelete size={16} /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
      {tab === 'waitlisted' && (
        waitlistedList.length === 0 ? (
          <p style={{ color: '#6c757d', margin: 0 }}>No waitlisted participants.</p>
        ) : (
          <>
            {!readOnly && selectedIds.size > 0 && onRemoveSelected && (
              <div style={{ marginBottom: '6px' }}>
                <button type="button" disabled={removing} onClick={onRemoveSelected} style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #dc3545', borderRadius: '4px', background: '#dc3545', color: '#fff', cursor: removing ? 'not-allowed' : 'pointer' }}>
                  {removing ? 'Removing…' : `Remove selected (${selectedIds.size})`}
                </button>
              </div>
            )}
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr style={theadTrStyle}>
                    {!readOnly && <th style={{ ...thStyle, width: '36px' }}><input type="checkbox" checked={waitlistedList.length > 0 && waitlistedList.every((p) => selectedIds.has(p._id))} onChange={(e) => onSelectAll && onSelectAll(waitlistedList, e.target.checked)} aria-label="Select all waitlisted" /></th>}
                    <th style={sortableThStyle('participant_name')} onClick={() => handleSort('participant_name')}>Participant{sortIndicator('participant_name')}</th>
                    <th style={sortableThStyle('registered_at')} onClick={() => handleSort('registered_at')}>Registered{sortIndicator('registered_at')}</th>
                    <th style={sortableThStyle('reg_type')} onClick={() => handleSort('reg_type')}>Reg Type{sortIndicator('reg_type')}</th>
                    <th style={sortableThStyle('attendance')} onClick={() => handleSort('attendance')}>Attendance{sortIndicator('attendance')}</th>
                    <th style={sortableThStyle('email')} onClick={() => handleSort('email')}>Email{sortIndicator('email')}</th>
                    <th style={sortableThStyle('phone')} onClick={() => handleSort('phone')}>Phone{sortIndicator('phone')}</th>
                    {!readOnly && onRemoveOne && (
                      <TableActionColumnHeader align="left" style={{ ...thStyle, width: '70px' }} />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedWaitlistedList.map((g) => (
                    <tr key={g._id} style={tbodyTrStyle}>
                      {!readOnly && <td style={tdStyle}><input type="checkbox" checked={selectedIds.has(g._id)} onChange={(e) => onSelectOne && onSelectOne(g._id, e.target.checked)} aria-label={`Select ${getParticipantDisplayName(g)}`} /></td>}
                      <td style={tdNameStyle}>{getParticipantDisplayName(g)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatParticipantRegisteredAt(g)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{(getParticipantRegTypeCell(g, eventForRegType) || '').trim() || '—'}</td>
                      <td style={tdStyle}>{(g.attendance || 'Waitlisted').trim()}</td>
                      <td style={tdStyle}>{displayEmailOrDash(g.email)}</td>
                      <td style={tdPhoneStyle}>{formatPhoneDisplay(g.phone)}</td>
                      {!readOnly && onRemoveOne && <td style={tdStyle}><button type="button" onClick={() => onRemoveOne(g._id)} style={{ ...iconBtnStyle, padding: '2px 6px', border: '1px solid #dc3545', borderRadius: '4px', background: '#fff', color: '#dc3545', cursor: 'pointer' }} title="Remove from waitlist"><IconDelete size={16} /></button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
      {tab === 'cancelled' && (
        cancelledList.length === 0 ? (
          <p style={{ color: '#6c757d', margin: 0 }}>No cancelled participants.</p>
        ) : (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadTrStyle}>
                  {!readOnly && <th style={{ ...thStyle, width: '36px' }}><input type="checkbox" checked={cancelledList.length > 0 && cancelledList.every((p) => selectedIds.has(p._id))} onChange={(e) => onSelectAll && onSelectAll(cancelledList, e.target.checked)} aria-label="Select all cancelled" /></th>}
                  <th style={sortableThStyle('participant_name')} onClick={() => handleSort('participant_name')}>Participant{sortIndicator('participant_name')}</th>
                  <th style={sortableThStyle('registered_at')} onClick={() => handleSort('registered_at')}>Registered{sortIndicator('registered_at')}</th>
                  <th style={sortableThStyle('reg_type')} onClick={() => handleSort('reg_type')}>Reg Type{sortIndicator('reg_type')}</th>
                  <th style={sortableThStyle('attendance')} onClick={() => handleSort('attendance')}>Attendance{sortIndicator('attendance')}</th>
                  <th style={sortableThStyle('email')} onClick={() => handleSort('email')}>Email{sortIndicator('email')}</th>
                  <th style={sortableThStyle('phone')} onClick={() => handleSort('phone')}>Phone{sortIndicator('phone')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedCancelledList.map((g) => (
                  <tr key={g._id} style={tbodyTrStyle}>
                    {!readOnly && <td style={tdStyle}><input type="checkbox" checked={selectedIds.has(g._id)} onChange={(e) => onSelectOne && onSelectOne(g._id, e.target.checked)} aria-label={`Select ${getParticipantDisplayName(g)}`} /></td>}
                    <td style={tdNameStyle}>{getParticipantDisplayName(g)}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatParticipantRegisteredAt(g)}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{(getParticipantRegTypeCell(g, eventForRegType) || '').trim() || '—'}</td>
                    <td style={tdStyle}>{g.attendance || 'Cancelled'}</td>
                    <td style={tdStyle}>{displayEmailOrDash(g.email)}</td>
                    <td style={tdPhoneStyle}>{formatPhoneDisplay(g.phone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

export { EventDetailsSummary, EventDetailsParticipantTabs };
